/** Leads hooks. API data only when authenticated (errors → isError + empty). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CLIENTS_QUERY_PREFIX,
  LEADS_QUERY_PREFIX,
  PROJECTS_QUERY_PREFIX,
  TASKS_QUERY_PREFIX,
  queryKeys,
} from "@/lib/query-client";
import type { Lead, LeadStageEntryView } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { leadsApi } from "./leads.api";
import {
  toLead,
  toLeadWriteBody,
  toStageEntry,
  type LeadInput,
  type TransitionInput,
} from "./leads.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export function useLeads(): {
  leads: Lead[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.leadsList(LIST_PARAMS),
    queryFn: () => leadsApi.list(LIST_PARAMS),
    enabled: authed,
    // The kanban is the most COLLABORATIVE surface (several people move leads
    // at once): refresh every 45s so a teammate's move appears without any
    // interaction. The list query is light (one page), and background
    // revalidation never blanks the board — no skeleton, no badge below 1.5s.
    refetchInterval: 45_000,
  });

  if (query.data)
    return {
      leads: query.data.data.map(toLead),
      isLoading: false,
      isError: false,
      source: "api",
      refetch: query.refetch,
    };
  if (query.isError)
    return { leads: [], isLoading: false, isError: true, source: "api", refetch: query.refetch };
  return {
    leads: [],
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    refetch: query.refetch,
  };
}

/** Spec §1.4 — the per-stage history feeding the timeline. */
export function useLeadHistory(leadId: string | undefined): {
  entries: LeadStageEntryView[];
  isLoading: boolean;
} {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const query = useQuery({
    queryKey: ["leads", "history", leadId ?? "none"],
    queryFn: () => leadsApi.history(leadId as string),
    enabled: authed && !!leadId,
  });
  return {
    entries: query.data ? query.data.data.map(toStageEntry) : [],
    isLoading: query.isLoading,
  };
}

/**
 * Refresh only leads data (list + per-stage history). Used by create/update/
 * archive/contact-log mutations that never touch clients, projects or tasks.
 */
function useInvalidateLeadsOnly() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: LEADS_QUERY_PREFIX });
    void qc.invalidateQueries({ queryKey: ["leads", "history"] });
  };
}

/**
 * Full fan-out for the activation / convert-to-client flow, which really does
 * create a client + project + tasks alongside the lead transition.
 */
function useInvalidateLeadsActivation() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: LEADS_QUERY_PREFIX });
    void qc.invalidateQueries({ queryKey: ["leads", "history"] });
    void qc.invalidateQueries({ queryKey: CLIENTS_QUERY_PREFIX });
    void qc.invalidateQueries({ queryKey: PROJECTS_QUERY_PREFIX });
    void qc.invalidateQueries({ queryKey: TASKS_QUERY_PREFIX });
    // ADR-001 : l'acompte d'activation poste une FinancialTransaction + un
    // Movement — la compta, la trésorerie et le dashboard doivent suivre.
    void qc.invalidateQueries({ queryKey: ["treasury"] });
    void qc.invalidateQueries({ queryKey: ["accounting"] });
    void qc.invalidateQueries({ queryKey: ["reports"] });
  };
}

export function useCreateLead() {
  const invalidate = useInvalidateLeadsOnly();
  return useMutation({
    mutationFn: async (input: LeadInput) => {
      return leadsApi.create(toLeadWriteBody(input));
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

/** Spec §1.3 — the only way a lead changes stage (mandatory pop-up payload).
 * Activation to a client stage creates a client + project + tasks server-side,
 * so this uses the full fan-out invalidation. */
export function useLeadTransition() {
  const invalidate = useInvalidateLeadsActivation();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: TransitionInput }) => {
      return leadsApi.transition(id, input);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useAddLeadContact() {
  const invalidate = useInvalidateLeadsOnly();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: { type: string; summary: string; followUpAt?: string };
    }) => {
      return leadsApi.addContact(id, { ...input });
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

/**
 * Spec §5 — prospects ARCHIVÉS, pour la permission « Restaurer ». Sans cette
 * liste, `leadsApi.restore` n'était appelable depuis aucun écran.
 */
export function useArchivedLeads(enabled: boolean) {
  const status = useAuthStore((s) => s.status);
  const params = { ...LIST_PARAMS, archived: true };
  const query = useQuery({
    queryKey: queryKeys.leadsList(params),
    queryFn: () => leadsApi.list(params),
    enabled: enabled && status === "authenticated",
  });
  return {
    leads: (query.data?.data ?? []).map(toLead),
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/** Spec §5 — « Restaurer » un prospect archivé. */
export function useRestoreLead() {
  const invalidate = useInvalidateLeadsOnly();
  return useMutation({
    mutationFn: (id: string) => leadsApi.restore(id),
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useArchiveLead() {
  const invalidate = useInvalidateLeadsOnly();
  return useMutation({
    mutationFn: async (id: string) => {
      return leadsApi.archive(id);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}
